import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import PromptCard from './prompt-display/PromptCard';
import TopicInterestField from './TopicInterestField';
import { listUserPrompts, provisionStarterPack } from '../../services/api';
import { COMMUNITY_JOB_FUNCTION_OPTIONS } from '../../constants/communityCategories';
import { useAuth } from '../../contexts/AuthContext';

export default function OnboardingStarterExplorer({ onOpenCapability, onExploreLibrary, onExploreAssistant }) {
  const { user, updateUserProfileData } = useAuth();
  const [topics, setTopics] = useState(() => (user?.jobFunctions || []).filter((topic) => COMMUNITY_JOB_FUNCTION_OPTIONS.includes(topic)));
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(true);
  const [resultNotice, setResultNotice] = useState('');
  const [retryRead, setRetryRead] = useState(false);
  const mounted = useRef(true);
  const selectedTopics = useRef(topics);
  selectedTopics.current = topics;

  const readStarters = async () => {
    const matches = [];
    let cursor;
    do {
      const page = await listUserPrompts({ limit: 100, ...(cursor ? { cursor } : {}) });
      matches.push(...(page.prompts || page.data || []).filter((item) => item.starterSourcePromptId && item.starterProvision?.kind !== 'onboarding'));
      cursor = page.nextCursor;
    } while (cursor);
    const relevant = (item) => (item.starterProvision?.starterTopics || []).some((topic) => selectedTopics.current.includes(topic));
    return matches.sort((a, b) => Number(relevant(b)) - Number(relevant(a)));
  };
  useEffect(() => {
    mounted.current = true;
    readStarters().then((items) => {
      if (!mounted.current) return;
      setCapabilities(items);
      setEditing(!items.length);
    }).catch((failure) => {
      if (mounted.current) { setError(failure.message || 'Could not load your Library.'); setRetryRead(true); }
    }).finally(() => { if (mounted.current) setLoading(false); });
    return () => { mounted.current = false; };
  }, []);
  const reload = async () => {
    setLoading(true); setError('');
    try { const items = await readStarters(); setCapabilities(items); setEditing(!items.length); setRetryRead(false); }
    catch (failure) { setError(failure.message || 'Could not load your capabilities. Please retry.'); }
    finally { setLoading(false); }
  };
  const addStarters = async () => {
    if (!topics.length) return;
    setLoading(true); setError(''); setResultNotice('');
    let added = false;
    try {
      await updateUserProfileData({ jobFunctions: topics });
      const result = await provisionStarterPack(topics);
      added = true;
      const count = (result.created || []).length;
      setResultNotice(count ? `${count} ${count === 1 ? 'capability added' : 'capabilities added'} to your Library.` : 'Your starter capabilities are already in your Library. No duplicates were added.');
      setEditing(false);
      // Provisioning succeeded even if reading the new cards fails.
      await updateUserProfileData({ viewedHelpers: { starterPackIntro: true } });
      setCapabilities(await readStarters());
      setRetryRead(false);
    } catch (failure) {
      setError(added ? 'Your starter pack was added, but we could not refresh this view. Retry loading your Library.' : failure.message || 'Could not add starter capabilities. Please try again.');
      setRetryRead(added);
    } finally { setLoading(false); }
  };
  return <Box>
    <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 1 }}>{capabilities.length ? 'Your starters, ready to explore' : 'Start with work that matters to you'}</Typography>
    <Typography sx={{ mb: 2, lineHeight: 1.65 }}>Your Capability Library is where you find, create, and organize reusable AI capabilities. Adapt their instructions to your work, then use them again in PromptCrew or your connected AI tool. {capabilities.length ? 'These starters are your own copies to explore and improve.' : 'Choose your interests to add everyday capabilities and others tailored to your work.'}</Typography>
    {editing ? <Box sx={{ maxWidth: 720 }}>
      <TopicInterestField value={topics} onChange={(value) => { setTopics(value); setError(''); }} disabled={loading} />
      <Button variant="contained" onClick={addStarters} disabled={loading || retryRead || !topics.length} sx={{ mt: 2 }}>Add starter capabilities</Button>
      {capabilities.length > 0 && <Button onClick={() => setEditing(false)} sx={{ mt: 2, ml: 1 }}>Cancel</Button>}
    </Box> : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      <Typography variant="body2">{topics.length ? topics.join(' / ') : 'Everyday capabilities'}</Typography><Button size="small" onClick={() => setEditing(true)}>Change interests</Button>
    </Box>}
    {loading && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}><CircularProgress size={18} />Loading your capabilities...</Box>}
    {resultNotice && <Alert severity="success" role="status" sx={{ mt: 2 }}>{resultNotice}</Alert>}
    {error && <Alert severity="error" sx={{ mt: 2 }} action={retryRead && <Button color="inherit" disabled={loading} onClick={reload}>Retry loading</Button>}>{error}</Alert>}
    {capabilities.length > 0 && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 3 }}>
      {capabilities.slice(0, 3).map((prompt) => <PromptCard key={prompt.id} prompt={prompt} onPromptClick={onOpenCapability} accessibleLabel={`Open ${prompt.title}`} hideActions hideStats hideMetadata />)}
    </Box>}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
      <Button onClick={onExploreLibrary}>View all in Library</Button>
      <Button onClick={onExploreAssistant}>Explore your Assistant</Button>
    </Box>
  </Box>;
}
